import { Box, Button, styled } from '@mui/material';
import { getQueryKey } from '@trpc/react-query';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { useLayoutContext } from 'contexts/LayoutContext';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { round, sumBy } from 'lodash';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useEqualisationSection } from '../EqualisationContext';
import EqualisationFooter from '../EqualisationFooter';
import EqualisationPage from '../EqualisationPage';
import EqualisationInterestCalculationsItem from './EqualisationInterestCalculationsItem';
import { EqualisationInterestFormValues } from './FormWrapper';
import NetEqualisationInterestPaymentTable from './NetEqualisationInterestPaymentTable';

const CalculationsItemWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const RowSpacer = styled(Box)({
  height: '32px',
});

interface EqualisationInterestContentProps {
  baseCurrency: CurrencyCode;
  calculationBreakdown: Array<{
    id: string;
    name: string;
    ledgerDate: CalendarDay;
    totalPayableValueLcy: number;
    newInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      workingCapital: number;
      returnOfCapital: number;
      netCallOrDistribution: number;
      payableValue: number;
      capitalContribution: number;
      cumulativePayableValue: number;
      equalisationInterestTotalLcy: number;
      equalisationInterestPercentage: number;
    }>;
  }>;
}

const EqualisationInterestContent: React.FC<
  EqualisationInterestContentProps
> = ({ baseCurrency, calculationBreakdown }) => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const { getValues, watch, reset } =
    useFormContext<EqualisationInterestFormValues>();
  const equalisationInterestPaymentBreakdown = watch(
    'equalisationInterestPaymentBreakdown',
  );

  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToPreviousSection,
    goToListPage,
  } = useEqualisationSection();

  const watchedBreakdown = watch('equalisationInterestPaymentBreakdown');
  const totalEqualisationInterestLcy =
    round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2) === 0
      ? 0
      : round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2);

  const equalisationInterestQueryKey = getQueryKey(
    api.equalisations.getInterest,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const [equalisationInterest] = api.equalisations.getInterest.useSuspenseQuery(
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationInterestQueryKey],
    }),
  );

  const handleReset = () => {
    reset({
      equalisationInterestPaymentBreakdown:
        equalisationInterest.netEqualisationInterestPaymentBreakdown.map(
          (item) => ({
            ...item,
            excludeFromEqualisationInterest: false,
          }),
        ),
    });
  };

  const [showCalculations, setShowCalculations] = useState(false);

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const columns: Array<TableColumn<(typeof calculationBreakdown)[number]>> = [
    {
      label: 'Name',
      key: 'name',
      render: ({ name }) => (
        <Row>
          <RowSpacer />
          <ColorIndicatorWithText text={name} colorKey={{ name }} />
        </Row>
      ),
    },
    {
      label: 'Ledger date',
      key: 'ledgerDate',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      label: `Total payable value (${baseCurrency})`,
      key: 'totalPayableValueLcy',
      render: (row) =>
        numberToCurrencyString(row.totalPayableValueLcy, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <EqualisationPage
      subheading="Process equalisation interest."
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          disabled={totalEqualisationInterestLcy !== 0}
          loading={updateEqualisation.isPending}
          tooltipDisabledMessage="Please ensure that the total equalisation interest payment breakdown equals 0."
        />
      }
    >
      <NetEqualisationInterestPaymentTable
        netEqualisationInterestPaymentBreakdown={
          equalisationInterestPaymentBreakdown
        }
        baseCurrency={baseCurrency}
        totalEqualisationInterestLcy={totalEqualisationInterestLcy}
      />

      <Row fullWidth justify="space-between">
        <TypographyButton
          disableRipple
          onClick={() => setShowCalculations(!showCalculations)}
          variant="body2"
          textDecoration="none"
          showExpandableStartAdornment
          isExpanded={showCalculations}
          text={
            showCalculations
              ? 'Hide full calculation breakdown'
              : 'Show full calculation breakdown'
          }
        />
        <Button size="large" variant="outlined" onClick={handleReset}>
          Reset interest totals
        </Button>
      </Row>

      {showCalculations && (
        <Table
          onRowExpand={({ id }) => toggleRowExpansion(id)}
          onRowClick={({ id }) => toggleRowExpansion(id)}
          isExpandedRow={({ id }) => expandedRows.has(id)}
          isExpandableRow={() => true}
          getExpandedContent={(row, _, index) => (
            <CalculationsItemWrapper key={index}>
              <EqualisationInterestCalculationsItem
                action={row}
                baseCurrency={baseCurrency}
                index={index}
                isLastElement={index === calculationBreakdown.length - 1}
              />
            </CalculationsItemWrapper>
          )}
          columns={columns}
          rows={calculationBreakdown}
        />
      )}
    </EqualisationPage>
  );
};

export default EqualisationInterestContent;
